import { useEffect, useState } from "react";
import { Wrench, Clock } from "lucide-react";

/**
 * Branded "We'll be back soon" page. Rendered by the SPA when
 * `/api/v1/maintenance/status` reports `enabled: true` for non-super-admin
 * users. Message + ETA come from the CMS (super_admin editable).
 */
export default function MaintenancePage({ status }) {
    const message = status?.message || "We're upgrading the platform and will be back very shortly. Thank you for your patience.";
    const eta = status?.eta;
    const [remaining, setRemaining] = useState(null);

    useEffect(() => {
        if (!eta) return;
        const compute = () => {
            const target = new Date(eta).getTime();
            const diff = target - Date.now();
            if (diff <= 0) { setRemaining("any moment now"); return; }
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            setRemaining(`${h > 0 ? h + "h " : ""}${m}m`);
        };
        compute();
        const t = setInterval(compute, 30000);
        return () => clearInterval(t);
    }, [eta]);

    return (
        <div className="min-h-screen ddi-hero-bg text-white flex items-center justify-center px-6" data-testid="maintenance-page">
            <div className="max-w-xl text-center relative">
                <div className="mx-auto h-16 w-16 rounded-full bg-white/10 flex items-center justify-center ring-4 ring-white/5 backdrop-blur-md">
                    <Wrench size={26} className="text-[#5EEAD4]" />
                </div>
                <div className="mt-8 text-[11px] uppercase tracking-[0.3em] text-[#5EEAD4] font-semibold">Scheduled maintenance</div>
                <h1 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight" data-testid="maintenance-heading">
                    We&apos;ll be back very soon.
                </h1>
                <p className="mt-5 text-white/75 text-base sm:text-lg leading-relaxed" data-testid="maintenance-message">
                    {message}
                </p>
                {remaining && (
                    <div className="mt-8 inline-flex items-center gap-2 text-sm bg-white/10 border border-white/15 rounded-full px-4 py-2 backdrop-blur-md" data-testid="maintenance-eta">
                        <Clock size={14} className="text-[#5EEAD4]" />
                        <span>Estimated back online in <b>{remaining}</b></span>
                    </div>
                )}
                <div className="mt-10 text-xs text-white/50">
                    Need urgent support? Contact <a href="mailto:support@doctorsprofile.in" className="underline text-white/70">support@doctorsprofile.in</a>.
                </div>
                <div className="mt-6 text-xs text-white/40">
                    Doctor Digital Identity — status page auto-refreshes every 30 seconds.
                </div>
            </div>
        </div>
    );
}
